import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import CreateProjectDropdown from '../DropdownStyle/CreateProjectDropdown';

/**
 * A dropdown component with checkbox selection optimized for table use
 * @param {Object} props Component props
 * @param {Array} props.options Array of options with label and value
 * @param {Array} props.selectedOptions Currently selected options
 * @param {Function} props.onChange Callback when selection changes
 * @param {string} props.placeholder Placeholder text when nothing is selected
 * @param {Object} props.styles Additional styles to apply
 * @param {boolean} props.opened Whether the dropdown is open (controlled externally)
 * @param {Function} props.onClick Callback when dropdown is clicked
 * @param {string} props.countLabel Label for selected count
 */
const TableCustomCheckboxDropdown = ({
  options = [],
  selectedOptions = [],
  onChange,
  placeholder = 'Select options',
  styles = {},
  opened = false,
  onClick,
  countLabel = 'items_selected',
  testId = 'table-custom-checkbox-dropdown',
  error = false,
  errorMessage,
  width,
}) => {
  const { t } = useTranslation();
  const dropdownRef = useRef(null);

  // Handle click on an option
  const handleOptionSelection = (option, e) => {
    if (e) {
      e.stopPropagation();
    }

    const isSelected = selectedOptions.some(o => o.value === option.value);
    let newSelection;

    if (isSelected) {
      newSelection = selectedOptions.filter(o => o.value !== option.value);
    } else {
      newSelection = [...selectedOptions, option];
    }

    onChange(newSelection);
  };

  // Handle click on the dropdown button
  const handleButtonClick = e => {
    e.preventDefault();
    e.stopPropagation();
    onClick && onClick();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = event => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        opened
      ) {
        onClick && onClick(); // Close the dropdown
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [opened, onClick]);

  return (
    <div
      data-testid={testId}
      style={{ width: width ? width : '100%', position: 'relative' }}
    >
      <div
        ref={dropdownRef}
        style={{ position: 'relative', ...styles.container }}
      >
        <button
          type="button"
          onClick={handleButtonClick}
          style={{
            ...CreateProjectDropdown().control({}, { menuIsOpen: opened }),
            ...(error && {
              border: '1px solid #fe5969',
              backgroundColor: 'rgba(252, 87, 104, 0.05)',
            }),
            width: '100%',
            textAlign: 'left',
            cursor: 'pointer',
            fontSize: '14px',
            padding: '8px 12px',
            ...styles.button,
          }}
        >
          {selectedOptions.length > 0
            ? `${selectedOptions.length} ${t(countLabel)}`
            : t(placeholder)}
        </button>
        {opened && (
          <div
            style={{
              ...CreateProjectDropdown().menu({}, { menuIsOpen: true }),
              position: 'absolute',
              top: '100%',
              left: 0,
              width: '100%',
              maxHeight: '200px',
              overflowY: 'auto',
              zIndex: 1000,
              ...styles.menu,
            }}
          >
            {options.map(option => (
              <div
                key={option.value}
                style={{
                  ...CreateProjectDropdown().option(
                    {},
                    {
                      isSelected: selectedOptions.some(
                        o => o.value === option.value,
                      ),
                    },
                  ),
                  display: 'flex',
                  alignItems: 'center',
                  padding: '8px 14px',
                  cursor: 'pointer',
                  ...styles.option,
                }}
                onClick={e => handleOptionSelection(option, e)}
              >
                {option.label}
              </div>
            ))}
          </div>
        )}
      </div>
      {error && errorMessage && (
        <div style={{ color: '#fe5969', fontSize: '12px', marginTop: '4px' }}>
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default TableCustomCheckboxDropdown;
